import React, { useMemo, useCallback, type ReactNode } from 'react';

import { HStack, Text, HStackProps } from '@frontend/legacy-components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

interface InternalLeafOptionProps<OptionType extends CascaderOption> {
  optionRef?: React.Ref<HTMLButtonElement>;
  option: OptionType;
  isHighlighted?: boolean;
  onClick: (option: OptionType) => void;
}

export interface LeafOptionProps<OptionType extends CascaderOption>
  extends HStackProps {
  option: OptionType;
  optionRef?: React.Ref<HTMLButtonElement>;
  getCommonProps: () => CascaderCommonProps<OptionType>;
  /** Optional content after the label (icons, badges); used by consumers that extend menu rows. */
  rightAddon?: ReactNode;
}

export const InternalLeafOption = <OptionType extends CascaderOption>({
  option,
  isHighlighted,
  onClick: internalOnClick,
  optionRef,
}: InternalLeafOptionProps<OptionType>) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { setHoverOption } = getCommonProps();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomLeafOption = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.LEAF_OPTION),
    [getComponent],
  );
  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      internalOnClick(option);
    },
    [internalOnClick, option],
  );
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);
  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setHoverOption(option);
    },
    [setHoverOption, option],
  );
  const onMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setHoverOption(null);
    },
    [setHoverOption],
  );

  return (
    <CustomLeafOption
      option={option}
      optionRef={optionRef}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.leafOption}
      role="menuitem"
      tabIndex={-1}
      data-highlighted={isHighlighted || undefined}
      aria-selected={isHighlighted}
      className="cascader-leaf-option"
    />
  );
};

export const LeafOption = <OptionType extends CascaderOption>({
  option,
  optionRef,
  getCommonProps: _,
  rightAddon,
  ...rest
}: LeafOptionProps<OptionType>) => {
  return (
    <HStack
      as="button"
      ref={optionRef}
      {...rest}
      justify="flex-start"
      spacing={1}
    >
      <Text size="md" as="span" flexGrow={1} minWidth={0} textAlign="left">
        {option.label}
      </Text>
      {rightAddon}
    </HStack>
  );
};
